import { navLabel, useI18n, useT } from '@kovix/shell-kit/i18n';
import { useToast } from '@kovix/ui';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
// Lưu ý: it.path đến từ routes.json (đã đăng ký trong router); ép kiểu '/' để thỏa kiểu `to` của TanStack Router.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { activeSupportToken, setSupportToken } from '@/app/auth/auth';
import { useSession } from '@/app/auth/session.store';
import { useMe, useRoleNames } from '@/entities/iam/queries';
import { filterNav, type NavGroup, PLATFORM_NAV, TENANT_NAV } from '@/shared/config/navigation';
import { TenantTimeZoneSync } from '@/shared/ui/TenantTimeZoneSync';
import { AccountMenu } from './AccountMenu';
import { CommandPalette } from './CommandPalette';
import { GlobalSearch } from './GlobalSearch';
import { HelpMenu } from './HelpMenu';
import { ShellIcon } from './NavIcon';
import { PortalLaunch } from './PortalLaunch';
import { RealtimeBell } from './RealtimeBell';
import { SidebarNav } from './SidebarNav';
import { TenantStatusBanner } from './TenantStatusBanner';
import { endOpsLanding, homePathOf, isActivePath, shellText } from './shellText';

const SIDEBAR_KEY = 'kovix.sidebar'; // 'collapsed' | 'open' — tiện ích theo trình duyệt, không phải state nghiệp vụ
const MOBILE_QUERY = '(max-width: 760px)';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === 'collapsed';
  } catch {
    return false;
  }
}

/**
 * Shell: xác thực, điều hướng, theme, bộ lọc xuyên suốt (TDD §9.1). Menu sinh từ routes.json, lọc theo quyền. W11: tìm kiếm toàn cục, chuông realtime, ngôn ngữ.
 * X0‑e (18/09/2026, mockup v150): nút thu gọn/mở menu (desktop thu gọn còn icon; < 760px là drawer + scrim, Esc đóng), command palette Ctrl K, nút Trợ giúp.
 * 01/10/2026 (mockup v152): logo ảnh trong thẻ trắng (bấm = về Trung tâm vận hành), nhóm menu thu/mở có badge số mục (`SidebarNav`), topbar
 * ☰ · "Trung tâm vận hành" (chỉ khi mục /ops còn sau filterNav) · "Vận hành / <màn>" · ô tìm Ctrl K · Customer & CTV Portal ↗ · ? · chuông · thẻ hồ sơ
 * (ngôn ngữ + đăng xuất chuyển vào menu tài khoản `AccountMenu`). CSS ở `packages/ui/src/styles/screens/shell.css` (tiền tố `kx-shell-`).
 */
export function Shell() {
  const session = useSession();
  const t = useT();
  const { locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);
  const [drawer, setDrawer] = useState(false);
  const [palette, setPalette] = useState(false);

  const nav: NavGroup[] = useMemo(() => {
    const src = session.mode === 'platform' ? PLATFORM_NAV : TENANT_NAV;
    return filterNav(src, { hasModule: session.hasModule, can: session.can, roles: session.permissions?.roles ?? [] });
  }, [session.mode, session.permissions, session.hasModule, session.can]);

  const current = useMemo(() => {
    const all = nav.flatMap((g) => g.items);
    return all.filter((i) => isActivePath(pathname, i.path)).sort((a, b) => b.path.length - a.path.length)[0] ?? null;
  }, [nav, pathname]);
  // T6 V-PF-25 (v152 topbar/rolebar): tên vai trò thay mã + chức danh — chỉ gọi iam tenant ở chế độ tenant (token platform không tới iam tenant)
  const tenantMode = session.status === 'ready' && session.mode === 'tenant';
  const { data: me } = useMe(tenantMode && !session.support);
  const roleNames = useRoleNames(
    session.permissions?.roles ?? [],
    tenantMode && session.can('admin.role.view'),
    session.permissions?.roleNames,
  );
  // v152 `.tenant-pill .tr` = "vai trò · tenant" (vd "Tenant Admin · Sài Gòn Food"); không có vai trò đọc được → chức danh
  const roleLabel = roleNames[0]
    ? `${roleNames[0]}${roleNames.length > 1 ? ` +${roleNames.length - 1}` : ''}`
    : (me?.title ?? '');
  const crumbRoot = shellText(session.mode === 'platform' ? 'crumbPlatform' : 'crumbTenant', locale);
  const crumbLeaf = current ? navLabel(current.key, current.label, locale) : t('shell.overview');
  const homePath = homePathOf(nav);
  const opsHome = homePath === '/ops';
  const homeLabel = shellText(opsHome ? 'opsCenterHint' : 'homeHint', locale);

  const toggleSidebar = useCallback(() => {
    if (window.matchMedia(MOBILE_QUERY).matches) {
      setDrawer((d) => !d);
      return;
    }
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(SIDEBAR_KEY, next ? 'collapsed' : 'open');
      } catch {
        /* bộ nhớ trình duyệt bị chặn — bỏ qua */
      }
      return next;
    });
  }, []);

  // Phím tắt toàn cục: Ctrl/⌘ K mở palette chức năng; Esc đóng drawer/palette
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPalette((p) => !p);
      } else if (e.key === 'Escape') {
        setDrawer(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  // R6.13 K-20: token phiên hỗ trợ không làm mới — hết exp (≤ 30′) thì rời chế độ hỗ trợ, quay về quyền platform (xin token mới ở /platform/support nếu phiên còn)
  const supportTokenActive = !!session.support?.tokenActive;
  const endSupport = session.endSupport;
  useEffect(() => {
    if (!supportTokenActive) return undefined;
    const timer = window.setInterval(() => {
      if (!activeSupportToken()) endSupport('expired');
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [supportTokenActive, endSupport]);
  // KOVIX-2981 (RJ-POP-05 B8): phiên hỗ trợ bị thu hồi (gateway 401 auth.support_session_ended) / hết hạn → báo rõ + đưa về phạm vi Platform (màn tenant đang mở sẽ là Không có quyền)
  const supportEnded = session.supportEnded;
  const ackSupportEnded = session.ackSupportEnded;
  useEffect(() => {
    if (!supportEnded) return;
    toast.push(
      supportEnded.reason === 'expired'
        ? `Token phiên hỗ trợ (ticket ${supportEnded.ticket}) đã hết hạn — đã quay về phạm vi Platform. Xin token mới ở Phiên hỗ trợ nếu phiên còn.`
        : `Phiên hỗ trợ (ticket ${supportEnded.ticket}) đã kết thúc hoặc bị thu hồi — đã quay về phạm vi Platform.`,
      'error',
    );
    ackSupportEnded();
    void navigate({ to: '/platform/support', search: { tab: 'mine', sessionId: supportEnded.sessionId } });
  }, [supportEnded, ackSupportEnded, toast, navigate]);
  // Đổi trang → đóng drawer mobile; rời `/` → hết lượt chuyển sang Trung tâm vận hành (R5.9)
  useEffect(() => {
    setDrawer(false);
    if (pathname !== '/') endOpsLanding();
  }, [pathname]);

  return (
    <div className={`kx-app kx-shell ${collapsed ? 'sidebar-collapsed' : ''} ${drawer ? 'sidebar-open' : ''}`}>
      <aside className="kx-sidebar" id="kx-sidebar" aria-label="Menu chính">
        <div className="kx-brand kx-shell-brand">
          <Link
            to={homePath as '/'}
            className="kx-shell-brand-card"
            title={homeLabel}
            aria-label={`KOVIX — ${homeLabel}`}
          >
            <img className="kx-shell-logo" src="/kovix-logo.png" alt="" width={1110} height={370} />
          </Link>
        </div>
        <SidebarNav nav={nav} pathname={pathname} locale={locale} collapsed={collapsed} />
        <div className="kx-sidebar-foot kx-shell-foot" title={session.permissions?.roles.join(', ')}>
          <span>v0.1</span>
          <span className="kx-shell-foot-role">{roleNames.join(', ') || '—'}</span>
        </div>
      </aside>
      <button
        type="button"
        className="kx-sidebar-scrim"
        aria-label={t('common.close')}
        tabIndex={drawer ? 0 : -1}
        onClick={() => setDrawer(false)}
      />

      <div className="kx-main">
        <header className="kx-topbar kx-shell-topbar">
          <button
            type="button"
            className="kx-shell-icon-btn kx-sidebar-toggle"
            title={t('shell.toggleSidebar')}
            aria-label={t('shell.toggleSidebar')}
            aria-controls="kx-sidebar"
            aria-expanded={drawer || !collapsed}
            onClick={toggleSidebar}
          >
            <ShellIcon name="menu" />
          </button>
          {opsHome ? (
            <Link
              to="/ops"
              className={`kx-shell-icon-btn kx-shell-home${pathname === '/ops' ? ' active' : ''}`}
              title={homeLabel}
              aria-label={homeLabel}
              aria-current={pathname === '/ops' ? 'page' : undefined}
            >
              <ShellIcon name="home" />
              <span className="kx-shell-home-text">{shellText('opsCenter', locale)}</span>
            </Link>
          ) : null}
          <nav className="kx-crumb kx-shell-crumb" aria-label="Breadcrumb">
            <Link to={homePath as '/'} className="kx-shell-crumb-home" title={homeLabel}>
              {crumbRoot}
            </Link>
            <span className="sep" aria-hidden="true">
              /
            </span>
            <b aria-current="page">{crumbLeaf}</b>
          </nav>
          {session.status === 'ready' ? <GlobalSearch onOpenPalette={() => setPalette(true)} /> : null}
          <div className="kx-top-actions kx-shell-actions">
            {session.status === 'ready' ? <PortalLaunch /> : null}
            <HelpMenu navKey={current?.key ?? null} />
            {session.status === 'ready' ? <RealtimeBell /> : null}
            {/* T6 (quyết định #5): đồng bộ múi giờ tenant cho ngày/giờ hiển thị — gắn MỘT lần ở Shell */}
            {session.status === 'ready' ? <TenantTimeZoneSync /> : null}
            <AccountMenu roleLabel={roleLabel} roleNames={roleNames} jobTitle={me?.title ?? null} />
          </div>
        </header>
        {session.support ? (
          <div className="kx-ctxbar" role="status">
            🛟 {session.support.access === 'read' ? t('shell.supportReadOnly') : t('shell.supportWrite')} · ticket{' '}
            {session.support.ticket} · {session.support.expiresAt}
            {session.support.tokenActive ? (
              <>
                {' · '}
                {session.support.sessionId ? (
                  <Link to="/platform/support" search={{ tab: 'mine', sessionId: session.support.sessionId }}>
                    Phiên hỗ trợ
                  </Link>
                ) : null}
                {' · '}
                {/* R6.13 K-20: rời chế độ hỗ trợ ở SPA (bỏ token mạo danh trong bộ nhớ); phiên ở platform-ops vẫn mở tới khi kết thúc/thu hồi */}
                <button
                  type="button"
                  className="kx-btn kx-btn-ghost kx-btn-sm"
                  onClick={() => {
                    setSupportToken(null);
                    session.endSupport();
                  }}
                >
                  Rời chế độ hỗ trợ
                </button>
              </>
            ) : null}
          </div>
        ) : null}
        {session.permissions?.sodConflicts.length ? (
          <div className="kx-ctxbar" role="status">
            ⚠ Tài khoản có {session.permissions.sodConflicts.length} xung đột phân tách nhiệm vụ (SoD) — Tenant Admin
            cần rà soát.
          </div>
        ) : null}
        {/* KOVIX-62 bước 6 (BR-18.123): tenant tạm ngưng / đang chấm dứt → banner toàn trang (chỉ khi có quyền đọc hồ sơ tenant) */}
        {/* KOVIX-380/63: người thiếu admin.tenant.overview vẫn thấy banner khi gateway trả 403 tenant.*_read_only */}
        <TenantStatusBanner
          enabled={tenantMode && !session.support && session.can('admin.tenant.overview')}
          visible={tenantMode && !session.support}
        />
        <main className="kx-content">
          <Outlet />
        </main>
      </div>
      <CommandPalette nav={nav} open={palette} onClose={() => setPalette(false)} />
    </div>
  );
}
